import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import type { ModuleType } from '../../types/demo';

interface PreviewCardProps {
  moduleType: ModuleType;
}

function ValidationCheck() {
  return (
    <div className="flex items-center gap-2 px-4 py-3 bg-green-50/60">
      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
      <div>
        <p className="text-xs font-semibold text-green-700">Validated</p>
        <p className="text-xs text-green-600/80">Totals verified against source document</p>
      </div>
    </div>
  );
}

// T12 Financials preview
function T12Preview() {
  const revenueRows = [
    { label: 'Market Rent', value: '$1,082,400', pct: '111.0%' },
    { label: 'Loss to Lease', value: '($32,472)', pct: '-3.3%' },
    { label: 'Vacancy', value: '($54,120)', pct: '-5.6%' },
    { label: 'Concessions / Bad Debt', value: '($21,015)', pct: '-2.2%' },
  ];

  const expenseRows = [
    { label: 'Payroll & Benefits', value: '$62,400', pct: '6.4%' },
    { label: 'General & Admin', value: '$18,200', pct: '1.9%' },
    { label: 'Repairs & Maintenance', value: '$38,400', pct: '3.9%' },
    { label: 'Contract Services', value: '$24,000', pct: '2.5%' },
    { label: 'Utilities', value: '$62,400', pct: '6.4%' },
    { label: 'Management Fee', value: '$48,737', pct: '5.0%' },
    { label: 'Property Taxes', value: '$98,400', pct: '10.1%' },
    { label: 'Insurance', value: '$28,800', pct: '3.0%' },
    { label: 'Replacement Reserves', value: '$18,000', pct: '1.8%' },
  ];

  return (
    <div className="flex flex-col flex-1">
      <div className="bg-gradient-to-r from-gsp-navy to-gsp-navy/90 px-4 py-4">
        <p className="text-xs text-white font-semibold">Oakwood Gardens Apartments</p>
        <p className="text-white/50 text-xs">4200 Riverside Dr, Austin, TX 78741</p>
      </div>

      <div className="flex items-center px-4 py-1.5 bg-gray-50 border-b border-gray-200">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-1">December 2025 T12</span>
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide w-[80px] text-right">Total</span>
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide w-[48px] text-right ml-5">%</span>
      </div>

      <div className="divide-y divide-gray-100">
        {revenueRows.map((r) => (
          <div key={r.label} className="flex items-center px-4 py-1">
            <span className="text-xs text-gray-600 flex-1">{r.label}</span>
            <span className={`text-xs w-[80px] text-right ${
              r.value.startsWith('(') ? 'text-gray-500' : 'text-gray-700 font-medium'
            }`}>
              {r.value}
            </span>
            <span className={`text-xs w-[48px] text-right ml-5 ${
              r.value.startsWith('(') ? 'text-gray-500' : 'text-gray-700'
            }`}>{r.pct}</span>
          </div>
        ))}
        <div className="flex items-center px-4 py-1.5 bg-gray-50 border-t border-gray-200">
          <span className="text-xs font-bold text-gsp-navy flex-1">Effective Gross Income</span>
          <span className="text-xs font-bold text-gsp-navy w-[80px] text-right">$974,793</span>
          <span className="text-xs font-bold text-gsp-navy w-[48px] text-right ml-5">100%</span>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {expenseRows.map((r) => (
          <div key={r.label} className="flex items-center px-4 py-1">
            <span className="text-xs text-gray-600 flex-1">{r.label}</span>
            <span className="text-xs text-gray-500 w-[80px] text-right">{r.value}</span>
            <span className="text-xs text-gray-500 w-[48px] text-right ml-5">{r.pct}</span>
          </div>
        ))}
        <div className="flex items-center px-4 py-1.5 bg-gray-50 border-t border-gray-200">
          <span className="text-xs font-bold text-gsp-navy flex-1">Total Operating Expenses</span>
          <span className="text-xs font-bold text-gsp-navy w-[80px] text-right">$399,337</span>
          <span className="text-xs font-bold text-gsp-navy w-[48px] text-right ml-5">41.0%</span>
        </div>
      </div>
      <div className="flex items-center px-4 py-2 bg-[#e8f4f8] border-t border-gray-300 flex-1">
        <span className="text-xs font-bold text-gsp-navy flex-1">Net Operating Income</span>
        <span className="text-xs font-bold text-gsp-navy w-[80px] text-right">$575,456</span>
        <span className="text-xs font-bold text-gsp-navy w-[48px] text-right ml-5">59.0%</span>
      </div>
    </div>
  );
}

// Rent Roll preview
function RentRollPreview() {
  const units = [
    { unit: '101', type: '1BR/1BA', tenant: 'Johnson, M.', start: '03/25', rent: '$1,850', status: 'Current' },
    { unit: '102', type: '2BR/2BA', tenant: 'Chen, L.', start: '07/24', rent: '$2,400', status: 'Current' },
    { unit: '103', type: 'Studio', tenant: '\u2014', start: '\u2014', rent: '$1,450', status: 'Vacant' },
    { unit: '104', type: '1BR/1BA', tenant: 'Patel, R.', start: '11/24', rent: '$1,875', status: 'Current' },
    { unit: '105', type: '2BR/1BA', tenant: 'Williams, T.', start: '01/25', rent: '$2,200', status: 'Current' },
    { unit: '106', type: '1BR/1BA', tenant: 'Garcia, A.', start: '09/24', rent: '$1,825', status: 'Current' },
    { unit: '107', type: 'Studio', tenant: 'Kim, J.', start: '05/25', rent: '$1,500', status: 'Current' },
    { unit: '108', type: '2BR/2BA', tenant: 'Davis, K.', start: '02/25', rent: '$2,350', status: 'Current' },
    { unit: '109', type: '1BR/1BA', tenant: 'Martinez, S.', start: '08/24', rent: '$1,900', status: 'Current' },
    { unit: '110', type: '2BR/1BA', tenant: '\u2014', start: '\u2014', rent: '$2,150', status: 'Vacant' },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="bg-gradient-to-r from-gsp-teal to-gsp-teal/85 px-4 py-4">
        <p className="text-xs text-white font-semibold">Maple Ridge Apartments</p>
        <p className="text-white/50 text-xs">1580 Harbor View Blvd, San Diego, CA 92101</p>
      </div>

      {/* Column headers */}
      <div className="flex bg-gray-50 border-b border-gray-200 text-xs text-gray-500 font-semibold">
        <span className="w-[46px] px-2.5 py-1.5 text-left">Unit</span>
        <span className="flex-1 px-1 py-1.5 text-center">Type</span>
        <span className="flex-1 px-1 py-1.5 text-center">Tenant</span>
        <span className="flex-1 px-1 py-1.5 text-center">Start</span>
        <span className="flex-1 px-1.5 py-1.5 text-center">Rent</span>
        <span className="flex-1 px-1.5 py-1.5 text-center">Status</span>
      </div>

      {/* Rows - flex-1 distributes space evenly */}
      <div className="flex-1 flex flex-col">
        {units.map((u, i) => (
          <div key={u.unit} className={`flex-1 flex items-center border-b border-gray-100 text-xs ${i % 2 === 1 ? 'bg-gray-50/50' : ''}`}>
            <span className="w-[46px] px-2.5 text-gray-700 font-medium">{u.unit}</span>
            <span className="flex-1 px-1 text-center text-gray-500">{u.type}</span>
            <span className="flex-1 px-1 text-center text-gray-600 truncate">{u.tenant}</span>
            <span className="flex-1 px-1 text-center text-gray-500">{u.start}</span>
            <span className="flex-1 px-1.5 text-center font-medium text-gray-700">{u.rent}</span>
            <span className="flex-1 px-1.5 text-center">
              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                u.status === 'Current' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
              }`}>
                {u.status}
              </span>
            </span>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center bg-gsp-navy/5 border-t border-gray-300 text-xs font-bold text-gsp-navy">
        <span className="flex-1 px-2.5 py-1.5">Total (10 units &middot; 80.0% occupied)</span>
        <span className="px-1.5 py-1.5 text-center">$19,500</span>
        <span className="flex-1 px-1.5"></span>
      </div>
    </div>
  );
}

// Lease Abstract preview - key terms + rent schedule (no validation)
function LeaseAbstractPreview() {
  const terms = [
    { label: 'Tenant', value: 'Acme Corp LLC' },
    { label: 'Landlord', value: 'Riverside Holdings LP' },
    { label: 'Premises', value: 'Suite 200, 12,500 SF' },
    { label: 'Commencement', value: 'Jan 1, 2024' },
    { label: 'Expiration', value: 'Dec 31, 2030' },
    { label: 'Expense Structure', value: 'NNN' },
    { label: 'Renewal Options', value: '2x 5-yr renewal' },
    { label: 'Security Deposit', value: '$87,500' },
    { label: 'TI Allowance', value: '$45.00 PSF' },
    { label: 'Free Rent', value: '3 months' },
    { label: 'ROFO/ROFR', value: 'ROFR on adjacent suite' },
    { label: 'Termination Option', value: 'End of Year 5 w/ penalty' },
  ];

  const rentSchedule = [
    { year: '1', dates: '1/24 – 12/24', psfYr: '$42.00', psfMo: '$3.50', annual: '$525,000' },
    { year: '2', dates: '1/25 – 12/25', psfYr: '$43.26', psfMo: '$3.61', annual: '$540,750' },
    { year: '3', dates: '1/26 – 12/26', psfYr: '$44.56', psfMo: '$3.71', annual: '$557,000' },
    { year: '4', dates: '1/27 – 12/27', psfYr: '$45.90', psfMo: '$3.83', annual: '$573,750' },
    { year: '5', dates: '1/28 – 12/28', psfYr: '$47.28', psfMo: '$3.94', annual: '$591,000' },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="bg-gradient-to-r from-gsp-navy to-gsp-navy/85 px-4 py-4">
        <p className="text-xs text-white font-semibold">Acme Corp LLC</p>
        <p className="text-white/50 text-xs">1 lease, 2 amendments processed</p>
      </div>

      {/* All data rows in one flex container for uniform height */}
      <div className="flex-1 flex flex-col">
        {terms.map((t, i) => (
          <div
            key={t.label}
            className={`flex-1 flex items-center justify-between px-4 border-b border-gray-100 ${i % 2 === 1 ? 'bg-gray-50/50' : ''}`}
          >
            <span className="text-xs font-semibold text-gsp-navy">{t.label}</span>
            <span className="text-xs text-gray-600">{t.value}</span>
          </div>
        ))}

        {/* Rent Schedule divider */}
        <div className="px-4 py-1.5 bg-gray-50 border-t border-gray-200 text-center">
          <p className="text-xs font-semibold text-gsp-navy uppercase tracking-wide">Rent Schedule</p>
        </div>
        {/* Column headers */}
        <div className="flex border-b border-gray-200 bg-gray-50/50 text-xs text-gray-500 font-semibold">
          <span className="w-[36px] px-2 py-1 text-left">Yr</span>
          <span className="flex-1 px-1 py-1 text-center">Period</span>
          <span className="flex-1 px-1 py-1 text-center">PSF/Yr</span>
          <span className="flex-1 px-1 py-1 text-center">PSF/Mo</span>
          <span className="flex-1 px-2 py-1 text-center">Annual</span>
        </div>

        {rentSchedule.map((r, i) => (
          <div key={r.year} className={`flex-1 flex items-center border-b border-gray-100 text-xs ${i % 2 === 1 ? 'bg-gray-50/30' : ''}`}>
            <span className="w-[36px] px-2 text-gray-700 font-medium">{r.year}</span>
            <span className="flex-1 px-1 text-center text-gray-500">{r.dates}</span>
            <span className="flex-1 px-1 text-center text-gray-700">{r.psfYr}</span>
            <span className="flex-1 px-1 text-center text-gray-700">{r.psfMo}</span>
            <span className="flex-1 px-2 text-center font-medium text-gray-700">{r.annual}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PreviewCard({ moduleType }: PreviewCardProps) {
  const hasValidation = moduleType !== 'lease-abstract';

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-full w-full flex flex-col">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50/80">
        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
          Sample Output
        </p>
      </div>

      <div className="flex-1 flex flex-col">
        <div className="flex-1 flex flex-col">
          {moduleType === 't12-financials' && <T12Preview />}
          {moduleType === 'rent-roll' && <RentRollPreview />}
          {moduleType === 'lease-abstract' && <LeaseAbstractPreview />}
        </div>

        {hasValidation && <ValidationCheck />}
      </div>
    </div>
  );
}

export default PreviewCard;
